import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import ProcessConfigurationForm from './ProcessConfigurationForm';
import type { ProcessConfigurationDto, UpdateProcessConfigurationRequest } from '@/types/configuration';

export default function ProcessConfigurationPage() {
  const { t } = useTranslation('processConfiguration');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['process-configuration'],
    queryFn: async () => (await apiClient.get<ProcessConfigurationDto>('/process-configuration')).data,
  });

  const [form, setForm] = useState<ProcessConfigurationDto | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateProcessConfigurationRequest) =>
      apiClient.put<ProcessConfigurationDto>('/process-configuration', payload),
    onSuccess: (response) => {
      setForm(response.data);
      queryClient.setQueryData(['process-configuration'], response.data);
    },
  });

  if (isLoading || !form) {
    return null;
  }

  const set = <K extends keyof ProcessConfigurationDto>(key: K, value: ProcessConfigurationDto[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = () => {
    if (!form) return;
    const { id: _id, companyId: _companyId, ...payload } = form;
    saveMutation.mutate(payload);
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.PROCESS_CONFIGURATION_MANAGE}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {t('save')}
            </Button>
          </IfPermitted>
        }
      />

      <ProcessConfigurationForm form={form} set={set} />
    </>
  );
}
